import React, { useState, useMemo, useEffect, useContext, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, Search, ShoppingCart, Home, ChevronRight, User, X,
  Plus, Minus, Package, Star, SlidersHorizontal,
  ChevronDown, ChevronUp, MapPin, Globe, RefreshCw,
  ShoppingBag, Tag,
  Zap,
} from "lucide-react";

import logo from "../../../assets/app/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../redux/productSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";
import { addToCart, decrementCartItem } from "../../../redux/cartSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";

import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";
import { Notifications, SupportAgent } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import {
  electronicsSubcategories,
  electronicsProducts,
} from "../../../UNIVERSAL/CATEGORIES/ElectronicsCategories";
import { fetchAllCommercePartners } from "../../../redux/commercePartnersSlice";
import { getContinent } from "../../../GLOBAL/geo";


/* ─────────────────────────────────────────────────────────────
   HOME APPLIANCES ONLY
   Top-level category id = 20000007
   Its subcategories become the "main categories" shown as cards
───────────────────────────────────────────────────────────── */
const HOME_APPLIANCES_ID = 20000007;

const applianceSubs = electronicsSubcategories.filter(
  (s) => s.parentId === HOME_APPLIANCES_ID
);

const applianceSubIds = new Set(applianceSubs.map((s) => s.id));

const applianceProds = electronicsProducts.filter((p) =>
  applianceSubIds.has(p.parentId as number)
);

const applianceProdIds = new Set(applianceProds.map((p) => p.id));

/* ═══════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════ */
const F = {
  pageBg:       "#FFFFFF",
  cardBg:       "#0D0520",
  gradH:        "linear-gradient(90deg,#1A0A3A 0%,#5B21B6 35%,#A78BFA 65%,#DDD6FE 100%)",
  gradAnim:     "linear-gradient(90deg,#0D0520 0%,#5B21B6 25%,#A78BFA 55%,#DDD6FE 80%,#5B21B6 100%)",
  amber:        "#5B21B6",
  amberDark:    "#1A0A3A",
  gold:         "#A78BFA",
  goldLight:    "#DDD6FE",
  cream:        "#EDE9FE",
  offwhite:     "#FAFAFA",
  muted:        "#8B7BAE",
  mutedDim:     "#4C3880",
  amberOutline: "1.5px solid rgba(167,139,250,0.50)",
  sidebarBg:    "#FFFFFF",
};

/* ═══════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{display:none;}
  input,button{font-family:inherit;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes mc-ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes mc-pulse{0%,100%{opacity:1}50%{opacity:.3}}

  input::placeholder{color:rgba(91,33,182,.35);}

  .f-card{transition:box-shadow .22s,transform .22s;}
  .f-card:hover{
    box-shadow:0 0 0 1.5px rgba(167,139,250,.6),0 12px 40px rgba(91,33,182,.22);
    transform:translateY(-4px);
  }

  .price-pill{
    display:inline-flex;align-items:center;
    background:linear-gradient(90deg,#0D0520,#5B21B6,#A78BFA,#DDD6FE);
    background-size:200% 100%;animation:gradSpin 3s ease infinite;
    border-radius:999px;padding:4px 14px;
    font-weight:900;color:#fff;letter-spacing:-0.4px;
    box-shadow:0 2px 12px rgba(91,33,182,.45);
  }

  .add-btn{
    border-radius:50%;border:none;cursor:pointer;
    background:linear-gradient(135deg,#5B21B6,#A78BFA,#DDD6FE);
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 3px 14px rgba(167,139,250,.45);
    transition:transform .18s,box-shadow .18s;
  }
  .add-btn:hover{transform:scale(1.12);box-shadow:0 6px 22px rgba(167,139,250,.65);}
  .add-btn:active{transform:scale(0.88);}

  .sort-item{
    padding:11px 18px;font-size:13px;cursor:pointer;
    display:flex;align-items:center;justify-content:space-between;
    border-bottom:1px solid rgba(91,33,182,.12);
    color:#FAFAFA;font-weight:500;transition:background .15s;
  }
  .sort-item:hover{background:rgba(91,33,182,.18);}
  .sort-item.active{color:#DDD6FE;font-weight:800;}

  .tab-item{display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;font-weight:700;cursor:pointer;color:#8B7BAE;padding:4px 10px;transition:color .2s;}
  .tab-item.active{color:#A78BFA;}

  .h-btn{
    display:flex;align-items:center;gap:6px;
    background:rgba(91,33,182,.08);
    border:1px solid rgba(91,33,182,.28);
    cursor:pointer;color:#1A0A3A;
    font-size:12px;font-weight:700;
    padding:7px 13px;border-radius:999px;
    flex-shrink:0;white-space:nowrap;
    transition:background .2s;
    font-family:inherit;
  }
  .h-btn:hover{background:rgba(91,33,182,.16);}

  /* ── subcategory card ── */
  .sub-card{
    display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;
    padding:16px 10px;border-radius:16px;
    border:1.5px solid rgba(167,139,250,.40);
    background:#FFFFFF;
    cursor:pointer;transition:all .2s;
    font-size:11px;font-weight:700;color:#1A0A3A;
    text-align:center;
  }
  .sub-card:hover{
    background:rgba(91,33,182,.06);
    border-color:rgba(91,33,182,.55);
    box-shadow:0 4px 16px rgba(91,33,182,.12);
  }
  .sub-card.active{
    background:rgba(91,33,182,.10);
    border-color:#5B21B6;
    color:#5B21B6;
  }
`;

/* ═══════════════════════════════════════
   LAYOUT TOKENS
═══════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page:       { minHeight:"100vh", background:F.pageBg, paddingBottom:90, fontFamily:"'Plus Jakarta Sans',sans-serif" },
  headerTop:  { background:"#fff", position:"sticky", top:0, zIndex:200, boxShadow:"0 2px 20px rgba(91,33,182,.12)", borderBottom:"1px solid rgba(91,33,182,.18)" },
  headerRow1: { display:"flex", alignItems:"center", gap:10, padding:"12px 16px 8px" },
  headerRow2: { display:"flex", alignItems:"center", gap:8, padding:"0 16px 10px", overflowX:"auto", scrollbarWidth:"none" as any },
  searchWrap: { display:"flex", alignItems:"center", flex:1, background:"rgba(91,33,182,.06)", borderRadius:999, overflow:"hidden", border:"1.5px solid rgba(91,33,182,.30)", transition:"border-color .2s,box-shadow .2s" },
  searchInput:{ flex:1, border:"none", outline:"none", padding:"10px 16px", fontSize:14, color:"#0D0520", background:"transparent", fontFamily:"inherit" },
  searchBtn:  { width:44, minHeight:42, background:F.gradH, border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, borderRadius:"0 999px 999px 0" },
  navOverlay: { position:"fixed" as const, inset:0, zIndex:599 },
  navMenu:    { position:"fixed" as const, top:72, right:16, width:230, zIndex:600, background:F.cardBg, borderRadius:18, boxShadow:"0 24px 64px rgba(0,0,0,.5),0 0 0 1px rgba(91,33,182,.3)", overflow:"hidden" },
  sortMenu:   { position:"fixed" as const, background:F.cardBg, borderRadius:14, boxShadow:"0 20px 56px rgba(0,0,0,.6),0 0 0 1px rgba(91,33,182,.3)", zIndex:9999, minWidth:210, overflow:"hidden" },
  grid:       { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:16 },
  mobileGrid: { display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12 },
  mobileTabs: { position:"fixed" as const, bottom:0, left:0, right:0, height:64, background:"#fff", display:"flex", justifyContent:"space-around", alignItems:"center", boxShadow:"0 -4px 24px rgba(91,33,182,.15)", zIndex:100, borderTop:"1px solid rgba(91,33,182,.18)" },
  sidebar:    { background:F.sidebarBg, borderRadius:20, padding:20, boxShadow:"0 4px 24px rgba(91,33,182,.10)", position:"sticky" as const, top:116, maxHeight:"calc(100vh - 130px)", overflowY:"auto" as const, border:F.amberOutline },
};

type SortKey = "default"|"price_asc"|"price_desc"|"newest"|"name_asc";
const SORT_OPTS: { key:SortKey; label:string }[] = [
  { key:"default",    label:"Best Match"       },
  { key:"newest",     label:"New Arrivals"      },
  { key:"price_asc",  label:"Price: Low → High" },
  { key:"price_desc", label:"Price: High → Low" },
  { key:"name_asc",   label:"Name: A → Z"       },
];

/* ═══════════════════════════════════════
   TICKER
═══════════════════════════════════════ */
const AppliancesTicker: React.FC<{ denied:boolean }> = ({ denied }) => {
  const msg = denied
    ? "⚠ LOCATION DENIED — SHOWING ALL APPLIANCES · ENABLE LOCATION FOR LOCAL BRANDS · "
    : "✦ HOME APPLIANCES — FRIDGES · WASHING MACHINES · COOKERS · MICROWAVES · FANS & MORE · ";
  return (
    <div style={{ overflow:"hidden", background: denied?"rgba(91,33,182,.08)":"rgba(167,139,250,.08)", borderBottom:`1px solid ${denied?"rgba(91,33,182,.22)":"rgba(167,139,250,.22)"}`, height:22, display:"flex", alignItems:"center" }}>
      <span style={{ display:"inline-block", whiteSpace:"nowrap", animation:"mc-ticker 26s linear infinite", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:9, fontWeight:700, letterSpacing:".18em", color: denied?F.amber:F.gold, paddingLeft:24 }}>{msg.repeat(6)}</span>
    </div>
  );
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
const HomeAppliances: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId     = authUser?.userId;

  const notifications = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount   = useMemo(() => notifications.filter((n:any) => !n.read).length, [notifications]);

  const filteredListings = useSelector((s: RootState) => s.products.filteredProducts);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const cartTotal        = cartItems.reduce((s:number, i:any) => s + i.qty, 0);

const ratingSummaries  = useSelector((s: RootState) => s.productRatings.summaries);
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile,            setIsMobile]           = useState(window.innerWidth <= 768);
  const [catOpen,             setCatOpen]            = useState(false);
  const [desktopCatOpen,      setDesktopCatOpen]     = useState(true);
  const [navOpen,             setNavOpen]            = useState(false);
  const [searchQuery,         setSearchQuery]        = useState("");
  const [searchFocused,       setSearchFocused]      = useState(false);
  const [sortOpen,            setSortOpen]           = useState(false);
  const [activeSort,          setActiveSort]         = useState<SortKey>("default");
  // activeSubcat = selected subcategory id (null = show all)
  const [activeSubcat,        setActiveSubcat]       = useState<number|null>(null);
  // activeProdGroup = selected product id within a subcategory
  const [activeProdGroup,     setActiveProdGroup]    = useState<number|null>(null);

  const sortBtnRef  = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top:number; left:number }|null>(null);

  const [userCountry,     setUserCountry]     = useState<string|null>(null);
  const [userCity,        setUserCity]        = useState<string|null>(null);
  const [userLat,         setUserLat]         = useState<number|null>(null);
  const [locationDenied,  setLocationDenied]  = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showGlobal,      setShowGlobal]      = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any|null>(null);
  const [selectedColor, setSelectedColor] = useState<number|null>(null);
  const [variantQty,    setVariantQty]    = useState(1);

  /* ── resize ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth guard ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data ── */
  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
    useEffect(() => { dispatch(fetchAllCommercePartners()); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null)); return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location:{ lat:pos.coords.latitude, lng:pos.coords.longitude } }, (results:any, status:string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t:string) => comps.find((c:any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        err => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name:"geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSubcat(null); setActiveProdGroup(null);
    dispatch(setFilters({ city:null, country:null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top:r.bottom + 6, left:r.left });
    }
    setSortOpen(true);
  };

  const resetFilters = () => {
    setActiveSubcat(null); setActiveProdGroup(null); setActiveSort("default");
    dispatch(setFilters({ city:null, country:null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
    dispatch(listenToPublishedProducts());
  };

  useEffect(() => { if (searchQuery.trim()) { setActiveSubcat(null); setActiveProdGroup(null); } }, [searchQuery]);

  const getQty = (id:string) => cartItems.find((i:any) => i.listingId === id)?.qty || 0;
  const hasVariants = (item:any) => item.variants && Object.keys(item.variants).length > 0;

  const handleAddToCart = (item:any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (hasVariants(item)) {
      setVariantItem(item); setSelectedColor(null); setVariantQty(1); setVariantOpen(true);
      return;
    }
    dispatch(addToCart({ listingId:item.listingId, name:item.name, price:item.rate, photo:item.photos?.[0], hostId:item.hostId, brandId:item.brandId }));
  };

  const handleVariantAdd = (toCheckout:boolean) => {
    if (!variantItem) return;
    dispatch(addToCart({
      listingId: variantItem.listingId,
      name:      variantItem.name,
      price:     variantItem.rate,
      photo:     variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId:   selectedColor ?? undefined,
      qty:       variantQty,
      hostId:    variantItem.hostId,
      brandId:   variantItem.brandId,
    }));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setVariantQty(1);
    if (toCheckout) goToCheckout();
  };

  const go           = (path:string) => { if (!userId) return; navigate(`/${path}/${userId}`); };
  const goToDetails  = (id:string)   => navigate(`/productdetails/${id}`);
  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/productcheckout/${userId}`, { state:{ userId, totalQty:cartItems.reduce((s:number,i:any)=>s+i.qty,0), totalPrice:cartItems.reduce((s:number,i:any)=>s+i.qty*i.price,0), items:cartItems } });
  };


  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCountry]);


  /* ── FILTERED PRODUCTS ── */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l:any) => {
      if (!l.isPublished) return false;

      // Must belong to Home Appliances category OR one of its subcats/products
      const inHomeAppliances =
        l.categoryId === HOME_APPLIANCES_ID ||
        applianceSubIds.has(l.subcategoryId) ||
        applianceProdIds.has(l.productId) ||
        applianceProdIds.has(l.productVariantId);

      if (!inHomeAppliances) return false;

      // Subcategory filter
      if (activeSubcat) {
        // Show only listings whose subcategoryId matches, or whose productId is in that subcat
        const prodsInSub = new Set(
          applianceProds.filter(p => p.parentId === activeSubcat).map(p => p.id)
        );
        const matchesSub =
          l.subcategoryId === activeSubcat ||
          prodsInSub.has(l.productId) ||
          prodsInSub.has(l.productVariantId);
        if (!matchesSub) return false;
      }

      // Product group filter (within a subcat)
      if (activeProdGroup) {
        if (l.productId !== activeProdGroup && l.productVariantId !== activeProdGroup) return false;
      }

      if (q && !`${l.name??""} ${l.description??""}`.toLowerCase().includes(q)) return false;
      return true;
    });
if (activeSort === "price_asc")  list = [...list].sort((a:any,b:any)=>(a.rate??0)-(b.rate??0));
    if (activeSort === "price_desc") list = [...list].sort((a:any,b:any)=>(b.rate??0)-(a.rate??0));
    if (activeSort === "name_asc")   list = [...list].sort((a:any,b:any)=>(a.name??"").localeCompare(b.name??""));
    if (activeSort === "newest")     list = [...list].sort((a:any,b:any)=>(b.createdAt??0)-(a.createdAt??0));
    if (activeSort === "default" && !showGlobal) {
      const tierOf = (l: any) => brandTier.get(l.brandId) ?? 3;
      list = [...list].sort((a, b) => tierOf(a) - tierOf(b));
    }
    return list;
  }, [filteredListings, activeSubcat, activeProdGroup, searchQuery, activeSort, showGlobal, brandTier]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    return order.map(bId => ({ brandId: bId, items: map.get(bId)! }));
  }, [filteredProducts]);


  /* ═════════════════════════════
     ANNOUNCEMENT BAR
  ═════════════════════════════ */
  const AnnouncementBar = () => {
    const isGlobal = locationDenied || showGlobal || (!locationLoading && userCountry === null);
    if (isGlobal) return <AppliancesTicker denied={locationDenied} />;
    return (
      <div style={{ background:F.gradH, backgroundSize:"200% 100%", animation:"gradSpin 4s ease infinite", color:"#fff", textAlign:"center", fontSize:10, padding:"7px 0", letterSpacing:"0.18em", fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:600 }}>
        ✦ FREE WORLDWIDE SHIPPING ON APPLIANCE ORDERS OVER €300
        {userCity && <span style={{ marginLeft:16, opacity:0.7 }}>· Near {userCity}</span>}
      </div>
    );
  };

  /* ═════════════════════════════
     HEADER FILTER ROW
  ═════════════════════════════ */
  const HeaderFilterRow = () => (
    <div style={{ ...S.headerRow2, padding:"12px 16px 8px", alignItems:"center", gap:8 }}>
      <div onClick={() => navigate(`/ProductsHome/${userId}`)} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", flexShrink:0, userSelect:"none" }}>
        <div style={{ position:"relative", display:"inline-flex" }}>
          <div style={{ position:"absolute", inset:-2, borderRadius:"50%", background:F.gradAnim, backgroundSize:"300% 100%", animation:"gradSpin 3s linear infinite", zIndex:0 }} />
          <div style={{ position:"relative", zIndex:1, background:"#fff", borderRadius:"50%", padding:2, display:"inline-flex" }}>
            <img src={logo} alt="METARELM" style={{ width:28, height:28, borderRadius:"50%", objectFit:"cover", display:"block" }} onError={e => { (e.currentTarget as HTMLImageElement).style.display="none"; }} />
          </div>
        </div>
        <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:16, fontWeight:900, background:F.gradAnim, backgroundSize:"300% 100%", animation:"gradSpin 3s linear infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>METARELM</span>
      </div>

      {locationLoading ? (
        <div style={{ display:"flex", alignItems:"center", gap:5, background:"rgba(91,33,182,.08)", borderRadius:999, padding:"6px 10px", flexShrink:0 }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:F.amber, animation:"mc-pulse 1.2s infinite" }} />
          <span style={{ fontSize:11, color:F.muted }}>Locating…</span>
        </div>
      ) : locationDenied ? (
        <button className="h-btn" style={{ color:F.amber, borderColor:"rgba(91,33,182,.3)" }}><MapPin size={12} /></button>
      ) : (
        <button onClick={handleToggleGlobal} className="h-btn" style={{ color: showGlobal?F.mutedDim:F.amber, borderColor: showGlobal?"rgba(91,33,182,.15)":"rgba(91,33,182,.40)" }}>
          {showGlobal ? <Globe size={12} /> : <MapPin size={12} />}
          <span style={{ display: isMobile?"none":"inline" }}>{showGlobal?"Global":userCity||userCountry||"Nearby"}</span>
        </button>
      )}

      <div style={{ flex:1 }} />

      {!isMobile && (
        <>
          <button onClick={() => navigate(`/clientProductOrder/${userId}`)} className="h-btn"><RefreshCw size={13} color={F.amber} /> Buy Again</button>
          <button onClick={() => navigate(`/CommerceOnboarding/${userId}`)} className="h-btn"><Tag size={13} color={F.gold} /> Sell</button>
          <button onClick={() => go("CustomerSupport")} className="h-btn"><SupportAgent style={{ fontSize:14 }} /> Support</button>
        </>
      )}

      <button onClick={() => isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v)} className="h-btn"
        style={{ background:"rgba(91,33,182,.12)", borderColor:"rgba(91,33,182,.40)", color:F.amberDark }}>
        <Menu size={14} /><span style={{ display: isMobile?"none":"inline" }}>Categories</span>
      </button>

      <button ref={sortBtnRef} onClick={toggleSort} className="h-btn"
        style={{ background: sortOpen?"rgba(91,33,182,.18)":"rgba(91,33,182,.06)", borderColor: sortOpen?F.amber:"rgba(91,33,182,.28)" }}>
        <SlidersHorizontal size={12} />
        <span style={{ display: isMobile?"none":"inline" }}>{activeSort !== "default" ? SORT_OPTS.find(o=>o.key===activeSort)?.label?.split(":")[0] : "Sort"}</span>
        {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {locationActive && (
        <button onClick={handleToggleGlobal} className="h-btn"
          style={{ color: showGlobal?F.mutedDim:F.gold, borderColor: showGlobal?"rgba(91,33,182,.15)":"rgba(167,139,250,.40)" }}>
          <Globe size={12} /><span style={{ display: isMobile?"none":"inline" }}>{showGlobal?"Global":"Local"}</span>
        </button>
      )}

      {(activeSubcat || activeProdGroup || activeSort !== "default") && (
        <button onClick={resetFilters} className="h-btn" style={{ color:F.cream, borderColor:"rgba(237,233,254,.35)" }}><X size={11} /></button>
      )}

      <button onClick={() => setNavOpen(v => !v)} className="h-btn">{navOpen ? <X size={18} /> : <Menu size={18} />}</button>

      {sortOpen && sortAnchor && (
        <>
          <div style={{ position:"fixed", inset:0, zIndex:9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ ...S.sortMenu, top:sortAnchor.top, left:sortAnchor.left }}>
            {SORT_OPTS.map(opt => (
              <div key={opt.key} className={`sort-item${activeSort===opt.key?" active":""}`}
                onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }}>
                {opt.label}{activeSort===opt.key && <span style={{ color:F.goldLight, fontWeight:900 }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  /* ═════════════════════════════
     HEADER SEARCH ROW
  ═════════════════════════════ */
  const HeaderSearchRow = () => (
    <div style={{ ...S.headerRow1, padding:"0 16px 12px", gap:8 }}>
      <div style={{ ...S.searchWrap, borderColor: searchFocused?F.amber:"rgba(91,33,182,.28)", boxShadow: searchFocused?"0 0 0 3px rgba(91,33,182,.14)":"none" }}>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} placeholder="Search home appliances…" style={S.searchInput} />
        {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background:"none", border:"none", cursor:"pointer", padding:"0 8px", color:F.muted }}><X size={14} /></button>}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      <div style={{ position:"relative", cursor:"pointer", flexShrink:0 }} onClick={() => go("notifications")}>
        <Notifications style={{ fontSize:24, color:F.amber }} />
        {unreadCount > 0 && <span style={{ position:"absolute", top:-5, right:-5, background:F.gold, color:"#fff", fontSize:9, fontWeight:900, width:17, height:17, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{unreadCount}</span>}
      </div>

      <button onClick={goToCheckout} className="h-btn" style={{ position:"relative", background: cartTotal>0?"rgba(91,33,182,.18)":"rgba(91,33,182,.06)", borderColor: cartTotal>0?F.amber:"rgba(91,33,182,.28)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && <span style={{ position:"absolute", top:-4, right:-4, background:F.gold, color:"#fff", fontSize:9, fontWeight:900, width:17, height:17, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{cartTotal>99?"99+":cartTotal}</span>}
      </button>

      {!isMobile && (
        <>
          <button onClick={() => navigate(`/ClientDashboard/${userId}`)} className="h-btn"><ShoppingBag size={16} /><span>Orders</span></button>
          <button onClick={() => go("profile")} className="h-btn"><User size={16} /></button>
        </>
      )}
    </div>
  );

  /* ═════════════════════════════
     NAV DROPDOWN
  ═════════════════════════════ */
  const NavDropdown = () => (
    <AnimatePresence>
      {navOpen && (
        <>
          <div style={S.navOverlay as React.CSSProperties} onClick={() => setNavOpen(false)} />
          <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} style={S.navMenu}>
            {[
              { label:"Profile", icon:<User size={15} />,           path:`/profile/${userId}` },
              { label:"Orders",  icon:<ShoppingBag size={15} />,    path:`/ClientDashboard/${userId}` },
              { label:"Sell",    icon:<Tag size={15} />,            path:`/CommerceOnboarding/${userId}` },
              { label:"Support", icon:<SupportAgent style={{ fontSize:15 }} />, path:`/CustomerSupport/${userId}` },
            ].map(item => (
              <button key={item.label}
                style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 18px", cursor:"pointer", fontSize:14, fontWeight:700, color:F.offwhite, background:"none", width:"100%", textAlign:"left", border:"none", borderBottom:"1px solid rgba(91,33,182,.15)", transition:"background .15s", fontFamily:"inherit" }}
                onClick={() => { navigate(item.path); setNavOpen(false); }}>
                <span style={{ color:F.gold }}>{item.icon}</span>{item.label}
              </button>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═════════════════════════════
     SUBCATEGORY CARDS
     These are the "main categories" for this page — rendered as
     a horizontal scroll (mobile) or sidebar grid (desktop).
     FIX: use a capitalized variable to render the icon component.
  ═════════════════════════════ */
  const SubcategoryCards = ({ horizontal = false }: { horizontal?: boolean }) => (
    <div style={horizontal
      ? { display:"flex", gap:10, overflowX:"auto", paddingBottom:4, scrollbarWidth:"none" }
      : { display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }
    }>
      {/* "All" card */}
      <div
        className={`sub-card${!activeSubcat ? " active" : ""}`}
        style={horizontal ? { minWidth:80, flexShrink:0 } : {}}
        onClick={() => { setActiveSubcat(null); setActiveProdGroup(null); setCatOpen(false); }}
      >
        <Zap size={22} color={!activeSubcat ? "#5B21B6" : F.muted} />
        <span style={{ color: !activeSubcat ? "#5B21B6" : "#1A0A3A" }}>All</span>
      </div>

      {applianceSubs.map((sub) => {
        const isActive = activeSubcat === sub.id;
        // ✅ FIX: assign to PascalCase variable so React renders it as a component
        const SubIcon = sub.icon;
        return (
          <div
            key={sub.id}
            className={`sub-card${isActive ? " active" : ""}`}
            style={horizontal ? { minWidth:80, flexShrink:0 } : {}}
            onClick={() => { setActiveSubcat(isActive ? null : sub.id); setActiveProdGroup(null); setCatOpen(false); }}
          >
            {SubIcon
              ? <SubIcon size={22} color={isActive ? "#5B21B6" : F.muted} />
              : <Zap size={22} color={isActive ? "#5B21B6" : F.muted} />
            }
            <span style={{ color: isActive ? "#5B21B6" : "#1A0A3A" }}>{sub.name}</span>
          </div>
        );
      })}
    </div>
  );

  /* ═════════════════════════════
     PRODUCT CHIPS (within an active subcategory)
     Shows individual product types to filter by
  ═════════════════════════════ */
  const ProductChips = () => {
    if (!activeSubcat) return null;
    const prods = applianceProds.filter(p => p.parentId === activeSubcat);
    if (!prods.length) return null;
    return (
      <div style={{ display:"flex", flexWrap:"wrap", gap:7, marginBottom:16 }}>
        {prods.map(p => (
          <div key={p.id}
            style={{ padding:"5px 13px", fontSize:12, fontWeight:700, borderRadius:999, cursor:"pointer",
              color: activeProdGroup===p.id ? "#fff" : F.amber,
              background: activeProdGroup===p.id ? F.amber : "rgba(91,33,182,.08)",
              border:`1.5px solid ${activeProdGroup===p.id ? F.amber : "rgba(91,33,182,.25)"}`,
              transition:"all .18s" }}
            onClick={() => setActiveProdGroup(activeProdGroup===p.id ? null : p.id)}>
            {p.name}
          </div>
        ))}
      </div>
    );
  };

  /* ═════════════════════════════
     MOBILE CATEGORY SHEET
  ═════════════════════════════ */
  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setCatOpen(false)}
            style={{ position:"fixed", inset:0, background:"rgba(13,5,32,.75)", zIndex:400 }} />
          <motion.div
            initial={{ y:"100%" }} animate={{ y:0 }} exit={{ y:"100%" }}
            transition={{ type:"spring", stiffness:340, damping:30 }}
            style={{ position:"fixed", bottom:0, left:0, right:0, maxHeight:"82vh", borderRadius:"22px 22px 0 0", background:"#fff", zIndex:401, borderTop:F.amberOutline, boxShadow:"0 -20px 60px rgba(167,139,250,.12)", overflowY:"auto" }}>
            <div style={{ width:36, height:4, background:"rgba(91,33,182,.4)", borderRadius:2, margin:"14px auto 0" }} />
            <div style={{ display:"flex", alignItems:"center", borderBottom:"1px solid rgba(91,33,182,.15)", padding:"12px 20px 10px" }}>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:11, letterSpacing:"0.15em", color:F.amber }}>✦ HOME APPLIANCE CATEGORIES</span>
              <div style={{ flex:1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background:"none", border:"none", cursor:"pointer" }}><X size={15} color={F.muted} /></button>
            </div>
            <div style={{ padding:"16px 16px 32px" }}>
              <SubcategoryCards horizontal={false} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );


  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Shop";
    const shopLogo = partner?.image || logo;

    return (
      <div style={{ display:"flex", alignItems:"center", gap:10, margin:"26px 0 12px" }}>
        <div style={{ width:34, height:34, borderRadius:"50%", overflow:"hidden", flexShrink:0, border:`1px solid rgba(124,58,237,0.50)`, background:"#fff" }}>
          <img src={shopLogo} alt={shopName} style={{ width:"100%", height:"100%", objectFit:"cover" }}
            onError={e => { (e.currentTarget as HTMLImageElement).src = logo; }} />
        </div>
        <span style={{ fontWeight:900, fontSize:14, color:"#0D0620", textDecoration:"underline", textDecorationColor:F.gold, textDecorationThickness:2, textUnderlineOffset:4 }}>
          {shopName}
        </span>
        <div style={{ flex:1 }} />
        <motion.button whileHover={{ scale:1.08 }} whileTap={{ scale:0.92 }}
          onClick={() => navigate(`/StoreHome/${brandId}`)}
          style={{ width:30, height:30, borderRadius:"50%", border:"none", background:"#fff",
            boxShadow:"0 3px 10px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.6)",
            display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
          <ChevronRight size={16} color={F.amber} />
        </motion.button>
      </div>
    );
  };


  /* ═════════════════════════════
     PRODUCT CARD
  ═════════════════════════════ */
  const ProductCard = ({ item, isMob }: { item:any; isMob:boolean }) => {
    const qty      = getQty(item.listingId);
    const price    = `${symbol||"$"}${((item.rate??0)*(rate??1)).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const summary  = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    return (
      <motion.div className="f-card" whileTap={{ scale:0.96 }}
        style={{ borderRadius:20, overflow:"hidden", background:F.cardBg, border:F.amberOutline, cursor:"pointer", display:"flex", flexDirection:"column" }}
        onClick={() => goToDetails(item.listingId)}>
        <div style={{ width:"100%", aspectRatio:"1/1", position:"relative", overflow:"hidden", background:"linear-gradient(135deg,rgba(91,33,182,0.12),rgba(167,139,250,0.06))" }}>
          {item.photos?.[0] ? (
            <motion.img src={item.photos[0]} alt={item.name}
              whileHover={{ scale:1.08 }} transition={{ type:"spring", stiffness:300, damping:26 }}
              style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
          ) : (
            <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Package size={isMob?36:44} color={F.amber} style={{ opacity:0.4 }} />
            </div>
          )}

          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(13,5,32,0.94) 0%,rgba(13,5,32,0.30) 42%,transparent 68%)", pointerEvents:"none" }} />

          {item.sold && item.sold > 0 && (
            <div style={{ position:"absolute", top:8, left:8, background:F.gradH, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", color:"#fff", fontSize:9, fontWeight:800, padding:"3px 9px", borderRadius:999, zIndex:5, boxShadow:"0 2px 8px rgba(91,33,182,.5)" }}>
              {item.sold>=1000?`${(item.sold/1000).toFixed(0)}K`:item.sold} sold
            </div>
          )}

          {discount && (
            <div style={{ position:"absolute", top:item.sold?30:8, left:8, background:F.gold, color:"#fff", fontSize:9, fontWeight:800, padding:"2px 8px", borderRadius:999, zIndex:5 }}>
              {discount}% OFF
            </div>
          )}

          {hasRating && (
            <div style={{ position:"absolute", top:8, right: isMob?50:56, zIndex:6, background:"rgba(13,5,32,.85)", backdropFilter:"blur(8px)", border:"1px solid rgba(91,33,182,.35)", borderRadius:999, padding: isMob?"3px 7px":"4px 9px", display:"flex", alignItems:"center", gap:3 }}>
              <Star size={isMob?9:10} fill={F.goldLight} color={F.goldLight} />
              <span style={{ color:F.cream, fontWeight:800, fontSize: isMob?9:10 }}>{summary!.avgRating.toFixed(1)}</span>
              <span style={{ color:F.mutedDim, fontSize: isMob?8:9 }}>({summary!.totalReviews.toLocaleString()})</span>
            </div>
          )}

          <div style={{ position:"absolute", top:8, right:8, zIndex:10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale:0.6, opacity:0 }} animate={{ scale:1, opacity:1 }}
                style={{ display:"flex", alignItems:"center", background:"rgba(91,33,182,.92)", borderRadius:999, padding: isMob?"3px 5px":"4px 6px", backdropFilter:"blur(8px)", border:"1.5px solid rgba(255,255,255,.2)", gap:0 }}>
                <button style={{ width: isMob?22:26, height: isMob?22:26, borderRadius:"50%", border:"none", background:"rgba(255,255,255,.18)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}
                  onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId:item.listingId })); }}>
                  <Minus size={isMob?8:10} color="#fff" />
                </button>
                <span style={{ color:"#fff", fontWeight:900, minWidth: isMob?18:22, textAlign:"center", fontSize: isMob?11:13 }}>{qty}</span>
                <button style={{ width: isMob?22:26, height: isMob?22:26, borderRadius:"50%", border:"none", background:"rgba(255,255,255,.28)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}
                  onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                  <Plus size={isMob?8:10} color="#fff" />
                </button>
              </motion.div>
            ) : (
              <motion.button className="add-btn" whileTap={{ scale:0.88 }}
                style={{ width: isMob?38:44, height: isMob?38:44 }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                <ShoppingCart size={isMob?15:18} color="#fff" />
              </motion.button>
            )}
          </div>

          <div style={{ position:"absolute", bottom:0, left:0, right:0, padding: isMob?"8px 10px 10px":"12px 14px 14px", zIndex:3, pointerEvents:"none" }}>
            <div style={{ fontWeight:800, fontSize: isMob?11:13, color:"#fff", lineHeight:1.25, marginBottom: isMob?5:7, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" as any, overflow:"hidden", textShadow:"0 1px 6px rgba(0,0,0,.8)", letterSpacing:-.2 }}>
              {item.name}
            </div>
            <span className="price-pill" style={{ fontSize: isMob?13:17 }}>{price}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  /* ═════════════════════════════
     RENDER PRODUCTS
  ═════════════════════════════ */
  const renderProducts = (isMob:boolean) => {
    const activeSubName = activeSubcat
      ? applianceSubs.find(s => s.id === activeSubcat)?.name
      : null;

    return (
      <div style={{ marginBottom:28 }}>
        {/* Product chips for active subcat */}
        <ProductChips />

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:4, height:22, borderRadius:2, background:F.gradH }} />
            <span style={{ fontWeight:900, fontSize:15, color:"#0D0520" }}>
              {activeSubName ?? (locationDenied?"All Appliances":showGlobal?"Global Appliances":userCity?`Near ${userCity}`:userCountry??"Home Appliances")}
            </span>
            <span style={{ fontSize:12, color:F.muted, background:"rgba(91,33,182,.10)", padding:"2px 9px", borderRadius:999 }}>{filteredProducts.length}</span>
          </div>
          {locationActive && (
            <button onClick={handleToggleGlobal}
              style={{ display:"flex", alignItems:"center", gap:5, background: showGlobal?"rgba(91,33,182,.06)":"rgba(91,33,182,.10)", borderRadius:999, padding:"5px 13px", border:`1.5px solid ${showGlobal?"rgba(91,33,182,.18)":"rgba(91,33,182,.35)"}`, fontSize:11, fontWeight:700, color: showGlobal?F.mutedDim:F.amber, cursor:"pointer", fontFamily:"inherit" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal?"Show Local":"Show Global"}
            </button>
          )}
        </div>

        {filteredProducts.length > 0 ? (
          <>
            {productsByBrand.map(group => (
              <div key={group.brandId}>
                <BrandRowHeader brandId={group.brandId} />
                <div style={isMob ? S.mobileGrid : S.grid}>
                  {group.items.map((item:any) => <ProductCard key={item.listingId} item={item} isMob={isMob} />)}
                </div>
              </div>
            ))}
          </>
        ) : (
          <div style={{ textAlign:"center", padding:56 }}>
            <div style={{ fontSize:52, marginBottom:14 }}>🏠</div>
            <h3 style={{ fontWeight:900, color:"#0D0520", marginBottom:8 }}>{locationDenied?"Location needed":"No appliances found"}</h3>
            <p style={{ color:F.muted, marginBottom:24, fontSize:13 }}>{locationDenied?"Enable location to see local brands.":"Try adjusting your filters."}</p>
            <button onClick={resetFilters}
              style={{ padding:"12px 28px", borderRadius:999, border:"none", background:F.gradH, color:"#fff", fontWeight:800, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
              View All Appliances
            </button>
          </div>
        )}
      </div>
    );
  };

  /* ═════════════════════════════
     VARIANT MODAL
  ═════════════════════════════ */
  const VariantModal = () => {
    const variantColorIds = Object.keys(variantItem?.variants ?? {}).map(Number);
    const previewImg = selectedColor && variantItem?.variants?.[selectedColor]?.imageUrl
      ? variantItem.variants[selectedColor].imageUrl
      : variantItem?.photos?.[0];
    const canContinue = variantColorIds.length === 0 || selectedColor !== null;

    const content = (
      <div style={{ background:"#fff", borderRadius: isMobile?"24px 24px 0 0":20, overflow:"hidden", maxWidth: isMobile?"100%":460, width:"100%", maxHeight: isMobile?"85vh":"80vh", display:"flex", flexDirection:"column" }}>
        <div style={{ height:4, background:F.gradH, backgroundSize:"200% 100%", animation:"gradSpin 4s ease infinite", flexShrink:0 }} />
        {isMobile && <div style={{ width:36, height:4, borderRadius:2, background:"rgba(91,33,182,.4)", margin:"12px auto 0", flexShrink:0 }} />}
        <div style={{ overflowY:"auto", padding:"20px 20px 0" }}>
          {previewImg && (
            <div style={{ width:"100%", aspectRatio:"16/7", borderRadius:14, overflow:"hidden", marginBottom:16, background:"#FAFAFA" }}>
              <img src={previewImg} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
            </div>
          )}
          <div style={{ fontSize:15, fontWeight:800, color:"#0D0520", marginBottom:4 }}>{variantItem?.name}</div>
          <div style={{ fontSize:20, fontWeight:900, background:F.gradH, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", marginBottom:18 }}>
            {symbol}{((variantItem?.rate??0)*(rate??1)).toFixed(2)}
          </div>
          {variantColorIds.length > 0 && (
            <>
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.18em", color:F.muted, marginBottom:12 }}>COLOUR / VARIANT</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:20 }}>
                {variantColorIds.map(cId => {
                  const v = variantItem?.variants?.[cId];
                  const isSel = selectedColor === cId;
                  return (
                    <button key={cId} onClick={() => setSelectedColor(cId)}
                      style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background: v?.colorHex??F.amber, border:`2.5px solid ${isSel?F.gold:"rgba(91,33,182,.2)"}`, outline: isSel?"3px solid rgba(167,139,250,.35)":"none", outlineOffset:2, transition:"transform .18s", transform: isSel?"scale(1.1)":"scale(1)" }} />
                      {isSel && <span style={{ fontSize:9, color:F.gold, fontWeight:700 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:20 }}>
            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:F.muted }}>QTY</span>
            <div style={{ display:"flex", border:"1.5px solid rgba(91,33,182,.22)", borderRadius:12, overflow:"hidden", background:"#FAFAFA" }}>
              <button onClick={() => setVariantQty(q => Math.max(1,q-1))} style={{ width:40, height:40, border:"none", background:"transparent", cursor:"pointer", fontSize:16, color:F.gold, fontFamily:"inherit" }}>−</button>
              <div style={{ width:44, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:800, color:"#0D0520", borderLeft:"1px solid rgba(91,33,182,.22)", borderRight:"1px solid rgba(91,33,182,.22)" }}>{variantQty}</div>
              <button onClick={() => setVariantQty(q => q+1)} style={{ width:40, height:40, border:"none", background:"transparent", cursor:"pointer", fontSize:16, color:F.gold, fontFamily:"inherit" }}>+</button>
            </div>
          </div>
        </div>
        <div style={{ padding:"14px 20px 20px", display:"flex", gap:10, flexShrink:0, borderTop:"1px solid rgba(91,33,182,.15)" }}>
          <button disabled={!canContinue} onClick={() => handleVariantAdd(false)}
            style={{ flex:1, padding:"13px 0", borderRadius:12, border:"1.5px solid rgba(91,33,182,.4)", background:"transparent", color:"#0D0520", fontWeight:700, fontSize:13, cursor: canContinue?"pointer":"not-allowed", opacity: canContinue?1:0.45, fontFamily:"inherit" }}>
            🛒 Add to Cart
          </button>
          <button disabled={!canContinue} onClick={() => handleVariantAdd(true)}
            style={{ flex:1, padding:"13px 0", borderRadius:12, border:"none", background: canContinue?F.gradH:"#B0A8C8", color:"#fff", fontWeight:800, fontSize:13, cursor: canContinue?"pointer":"not-allowed", fontFamily:"inherit" }}>
            ✦ Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        {variantOpen && (
          <>
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              onClick={() => { setVariantOpen(false); setVariantItem(null); setSelectedColor(null); }}
              style={{ position:"fixed", inset:0, background:"rgba(13,5,32,.75)", zIndex:500, backdropFilter:"blur(6px)" }} />
            {isMobile ? (
              <motion.div initial={{ y:"100%" }} animate={{ y:0 }} exit={{ y:"100%" }}
                transition={{ type:"spring", stiffness:340, damping:30 }}
                style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:501 }}>
                {content}
              </motion.div>
            ) : (
              <div style={{ position:"fixed", inset:0, zIndex:501, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                <motion.div initial={{ scale:0.95, opacity:0, y:10 }} animate={{ scale:1, opacity:1, y:0 }} exit={{ scale:0.95, opacity:0, y:10 }}
                  transition={{ type:"spring", damping:26, stiffness:340 }}
                  style={{ width:460, pointerEvents:"all", borderRadius:20, overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,.35),0 0 0 1.5px rgba(167,139,250,.35)" }}>
                  {content}
                </motion.div>
              </div>
            )}
          </>
        )}
      </AnimatePresence>
    );
  };

  /* ═════════════════════════════
     MOBILE BOTTOM TABS
  ═════════════════════════════ */
  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon:<Home size={22} />,        label:"Home",       active:false, action:() => navigate(`/ProductsHome/${userId}`) },
        { icon:<Zap size={22} />,          label:"Appliances", active:true,  action:() => {} },
        { icon:<ShoppingCart size={22} />, label:"Cart",       active:false, action:goToCheckout, badge:cartTotal },
        { icon:<ShoppingBag size={22} />,  label:"Orders",     active:false, action:() => navigate(`/ClientDashboard/${userId}`) },
        { icon:<User size={22} />,         label:"Profile",    active:false, action:() => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale:0.88 } as any} onClick={t.action}
          className={`tab-item${t.active?" active":""}`} style={{ position:"relative" }}>
          <div style={{ position:"relative" }}>
            {React.cloneElement(t.icon as any, { color: t.active?F.gold:F.mutedDim })}
            {(t as any).badge > 0 && (
              <span style={{ position:"absolute", top:-5, right:-5, background:F.gold, color:"#fff", fontSize:8, fontWeight:900, width:16, height:16, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{(t as any).badge}</span>
            )}
          </div>
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═════════════════════════════
     RENDER
  ═════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>

      <AnnouncementBar />

      <div style={S.headerTop}>
        <HeaderFilterRow />
        <HeaderSearchRow />
      </div>

      <NavDropdown />
      <MobileCategorySheet />

      {/* ── DESKTOP LAYOUT ── */}
      {!isMobile && (
        <div style={{ display:"grid", gap:24, padding:"24px 24px 0", gridTemplateColumns: desktopCatOpen?"260px 1fr":"1fr" }}>
          {desktopCatOpen && (
            <aside style={S.sidebar}>
              <div style={{ fontSize:11, fontWeight:800, letterSpacing:"0.15em", color:F.amber, marginBottom:16 }}>✦ HOME APPLIANCES</div>
              <SubcategoryCards horizontal={false} />
            </aside>
          )}
          <main style={{ minWidth:0 }}>
            {renderProducts(false)}
          </main>
        </div>
      )}

      {/* ── MOBILE LAYOUT ── */}
      {isMobile && (
        <div style={{ padding:"14px 12px 0" }}>
          {/* Horizontal scroll category strip */}
          <div style={{ marginBottom:16 }}>
            <SubcategoryCards horizontal={true} />
          </div>
          {renderProducts(true)}
        </div>
      )}

      <VariantModal />
      <BottomTabs />
    </div>
  );
};

export default HomeAppliances;